import React, { useEffect, useState, useRef } from "react";
import { CalendarBlank, X } from "@phosphor-icons/react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

// -----------------------------------------------------------------------------
// DatePicker
// Drop-in replacement for <Input type="date"/> that uses Shadcn Calendar (v8
// react-day-picker) inside a Popover for a consistent, on-brand experience.
//
// Props (mirrors <Input> for easy adoption):
//   value       "YYYY-MM-DD" or "" — controlled value
//   onChange    (e) => void  — receives event-like { target: { value } }
//   min         "YYYY-MM-DD" — disables dates before this
//   max         "YYYY-MM-DD" — disables dates after this
//   required    boolean
//   disabled    boolean
//   placeholder string
//   className   string
//   "data-testid" string
// -----------------------------------------------------------------------------

function parseISO(s) {
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const [_, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  return isNaN(dt.getTime()) ? null : dt;
}

function toISO(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function formatDisplay(iso) {
  const d = parseISO(iso);
  if (!d) return "";
  // Compact readable format: Wed, 12 Feb 2026
  return d.toLocaleDateString("en-IN", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
}

export function DatePicker({
  value = "",
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  placeholder = "Pick a date",
  className = "",
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const selected = parseISO(value);
  const minDate = parseISO(min);
  const maxDate = parseISO(max);
  const testId = rest["data-testid"];

  const fire = (iso) => {
    if (!onChange) return;
    onChange({ target: { value: iso, name: rest.name || "" } });
  };

  const handleSelect = (d) => {
    if (!d) return;
    fire(toISO(d));
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    fire("");
  };

  const disabledRules = [];
  if (minDate) disabledRules.push({ before: minDate });
  if (maxDate) disabledRules.push({ after: maxDate });

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          aria-required={required || undefined}
          className={
            "w-full px-3 py-2 border border-zinc-300 bg-white text-sm text-left " +
            "flex items-center justify-between gap-2 focus:outline-none " +
            "focus:border-orange-600 focus:ring-1 focus:ring-orange-600 " +
            (disabled ? "opacity-60 cursor-not-allowed " : "hover:border-zinc-400 cursor-pointer ") +
            className
          }
        >
          <span className={value ? "text-zinc-900 font-mono" : "text-zinc-400"}>
            {value ? formatDisplay(value) : placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {value && !disabled && !required && (
              <span
                onClick={clear}
                className="text-zinc-400 hover:text-zinc-700 p-0.5"
                data-testid={testId ? `${testId}-clear` : undefined}
                title="Clear"
              >
                <X size={12} weight="bold" />
              </span>
            )}
            <CalendarBlank size={14} weight="bold" className="text-zinc-500" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        data-testid={testId ? `${testId}-pop` : undefined}
      >
        <Calendar
          mode="single"
          selected={selected || undefined}
          onSelect={handleSelect}
          defaultMonth={selected || minDate || new Date()}
          disabled={disabledRules.length ? disabledRules : undefined}
          initialFocus
          weekStartsOn={1}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
